import { Period                       } from './util.js';
import { getDurationStrBetweenMoments } from './util.js';
import { convertMomentToStamp         } from './util.js';
import { convertUintTo2PosStr         } from './util.js';
import { getAmountStr                 } from './util.js';

export class YearMonthPeriod {
  /**
   * @param {YearMonth} begin
   * @param {YearMonth} end
   */
  constructor(begin, end) {
    this.begin = begin;
    this.end = end;
  }

  getBegin() {
    return this.begin;
  }

  getEnd() {
    return this.end;
  }
}

/**
 * @typedef {Object} YearMonthPeriodObject
 * @property {string} begin
 * @property {string} end
 */

/**
 * 
 * @param {YearMonthPeriodObject} periodObject
 * @returns {?YearMonthPeriod}
 */
export function convertObjectToYearMonthPeriod(periodObject) {
  const begin = parseYearMonth(periodObject.begin);
  if(begin === null) {
    return null;
  }

  const end = parseYearMonth(periodObject.end);
  if(end === null) {
    return null;
  }

  return new YearMonthPeriod(begin, end);
}

/**
 * @param {YearMonthPeriod} period 
 * @returns {string}
 */
export function convertYearMonthPeriodToInclusiveString(period) {
  const endMonth = period.getEnd();
  const lastMonth = endMonth.getPrev();
  return period.begin.toString() + '_' + lastMonth.toString();
}

//-----------------------------------------------------------------------------

const JANUARY  =  1;
const DECEMBER = 12;

export class YearMonth {
  /**
   * @param {number} year
   * @param {number} month 1-12
   */
  constructor(year, month) {
    /** @type {number} */
    this.year = year;

    /** @type {number} */
    this.month = month;
  }

  getYear() {
    return this.year;
  }

  getMonth() {
    return this.month;
  }

  /**
   * @param {YearMonth} other 
   * @returns {boolean}
   */
  isEqual(other) {
    return (this.year === other.year) && (this.month == other.month);
  }

  /**
   * @param {YearMonth} other 
   * @returns {boolean}
   */
  isBefore(other) {
    if(this.year < other.year) {
      return true;
    }
    if(this.year > other.year) {
      return false;
    }
    return this.month < other.month;
  }

  getPrev() {
    const isFirstMonth = (this.month === JANUARY);
    if(isFirstMonth) {
      return new YearMonth(this.year-1, DECEMBER);
    }
  
    return new YearMonth(this.year, this.month-1);
  }

  getNext() {
    const isLastMonth = (this.month === DECEMBER);
    if(isLastMonth) {
      return new YearMonth(this.year+1, JANUARY);
    }
  
    return new YearMonth(this.year, this.month+1);
  }

  toString() {
    return this.toISOString();
  }

  toISOString() {
    const yearStr = this.year.toString();

    const monthStr = this.month.toString();
    const formattedMonthStr = monthStr.padStart(2, '0');

    return yearStr + '-' + formattedMonthStr;
  }
}

/**
 * @param {string} str Example: '2021-09'
 * @returns {?YearMonth}
 */
export function parseYearMonth(str) {
  if(str === undefined) {
    // invalid input
    return null;
  }
  if(str === null) {
    // invalid input
    return null;
  }

  const parts = str.split('-');
  if(parts.length !== 2) {
    // invalid format
    return null;
  }

  const year = parseInt(parts[0], 10);
  if(isNaN(year)) {
    // invalid format
    return null;
  }

  const month = parseInt(parts[1], 10);
  if(isNaN(month)) {
    // invalid format
    return null;
  }

  return new YearMonth(year, month);
}

//-----------------------------------------------------------------------------

/**
 * @typedef {Object} DatePeriodObject
 * @property {Date} begin
 * @property {Date} end
 */


/**
 * @param {DatePeriodObject} period 
 * @returns {string}
 */
export function DatePeriodObject_getDurationStr(period) {
  return getDurationStrBetweenMoments(period.begin, period.end);
}

/**
 * 
 * @param {DatePeriodObject} datePeriodObject 
 * @returns {Period}
 */
export function DatePeriodObject_toStampPeriod(datePeriodObject) {
  const beginSec = convertMomentToStamp(datePeriodObject.begin);
  const endSec   = convertMomentToStamp(datePeriodObject.end);
  return new Period(beginSec, endSec);
}

export function convertDurationSecToMMSSStr(durationSec) {
  if(durationSec < 0) {
    return 'invalid duration sec';
  }

  if(durationSec === 0) {
    return '00:00';
  }

  const SEC_IN_DAY = 24 * 60 * 60;
  const SEC_IN_H   =      60 * 60;
  const SEC_IN_MIN =           60;

  const dayAmount = Math.floor(durationSec / SEC_IN_DAY);

  const dayRemainderSec = durationSec - dayAmount * SEC_IN_DAY;
  const hAmount = Math.floor(dayRemainderSec / SEC_IN_H);

  const hRemainderSec = dayRemainderSec - hAmount * SEC_IN_H;
  const minAmount = Math.floor(hRemainderSec / SEC_IN_MIN);

  const minRemainderSec = hRemainderSec - minAmount * SEC_IN_MIN;
  const secAmount = minRemainderSec;

  const hStr   = convertUintTo2PosStr(hAmount);
  const minStr = convertUintTo2PosStr(minAmount);
  const secStr = convertUintTo2PosStr(secAmount);

  let timeStr = minStr + ':' + secStr;

  if(hStr > 0 || dayAmount > 0) {
    timeStr = hStr + ':' + timeStr;
  }

  const parts = [];
  if(dayAmount > 0) {
    parts.push(getAmountStr(dayAmount, 'day', 'days'));
  }

  parts.push(timeStr);

  return parts.join(' ');
}

//-----------------------------------------------------------------------------

export function getStampSubDays(stamp, dayAmount) {
  const moment = new Date(stamp * 1000);
  moment.setDate(moment.getDate() - dayAmount);
  return convertMomentToStamp(moment);
}

export function getStampSubMonths(stamp, monthAmount) {
  const moment = new Date(stamp * 1000);
  moment.setMonth(moment.getMonth() - monthAmount);
  return convertMomentToStamp(moment);
}

export function getStampAddDays(stamp, dayAmount) {
  const moment = new Date(stamp * 1000);
  moment.setDate(moment.getDate() + dayAmount);
  return convertMomentToStamp(moment);
}

export function getStampAddMonths(stamp, monthAmount) {
  const moment = new Date(stamp * 1000);
  moment.setMonth(moment.getMonth() + monthAmount);
  return convertMomentToStamp(moment);
}

//-----------------------------------------------------------------------------
